import React, { useEffect, useRef, useState } from "react";
import { CgSpinner } from "react-icons/cg";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { New_ScrollX2025 } from "../../../assets/icons";
import CustomToast, { defaultcustomToatStyle } from "../../../Components/CustomToast/CustomToast";
import {
  CancellSubscription,
  getCurrentPlan,
  getRendersPlan,
} from "../../../Redux/actions/commonActtion";
import { logout } from "../../../Redux/actions/authActions";
import { getProjects } from "../../../Redux/actions/projectsActions";
import { getRequest, postRequest } from "../../../services/axios";
import "../../UserPlan/ChoosePlanStyle.css";
import CancelSubscriptionModal from "./CancelSubscriptionModal";
import UpgradeConfirmModal from "./UpgradeConfirmModal";
import { IoMdArrowRoundBack } from "react-icons/io";

const formatCardBrand = (brand) => {
  if (!brand) return "Card";
  return brand.charAt(0).toUpperCase() + brand.slice(1);
};

const formatCardExpiry = (expMonth, expYear) => {
  if (!expMonth || !expYear) return null;
  return `${String(expMonth).padStart(2, "0")}/${String(expYear).slice(-2)}`;
};

/* ─── helpers ───────────────────────────────────────────────── */
const formatExpiryDate = (date) => {
  if (!date) return "";
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

function formatDateTime(isoString) {
  if (!isoString) return "N/A";
  const date = new Date(isoString);
  const options = { month: "short", day: "numeric", year: "numeric" };
  const formattedDate = date.toLocaleDateString("en-US", options);
  return formattedDate;
}

function addIntervalToPurchaseDate(planPurchaseDate, interval_count, interval_type) {
  if (!planPurchaseDate) return null;
  const purchaseDate = new Date(planPurchaseDate);
  if (isNaN(purchaseDate)) return null;
  switch (interval_type) {
    case "day":   purchaseDate.setDate(purchaseDate.getDate() + interval_count); break;
    case "month": purchaseDate.setMonth(purchaseDate.getMonth() + interval_count); break;
    case "year":  purchaseDate.setFullYear(purchaseDate.getFullYear() + interval_count); break;
    default:      return null;
  }
  return purchaseDate;
}

/* ─── icons (inline SVG so no extra deps) ───────────────────── */
const CheckCircleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c5cff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><path d="M9 12l2 2 4-4"/>
  </svg>
);

const CreditCardIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7c5cff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>
  </svg>
);

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);
const MoreInfoPage = () => {
  const userInfo = JSON.parse(localStorage.getItem("user"));
  const navigate  = useNavigate();
  const dispatch  = useDispatch();
  const location  = useLocation();

  const { isDashboard, isCard, projectId, isMoreInfo, isRender } = location?.state || {};

  const pathSegments = location.pathname.split("/");
  const segment      = pathSegments[1];

  const project = useSelector((state) => state?.project);

  const [userStripeData,    setUserStripeData]    = useState([]);
  const [paymentMethod,     setPaymentMethod]     = useState(null);
  const [isLoading,         setIsLoading]         = useState(false);
  const [isUpdatingCard,    setIsUpdatingCard]    = useState(false);
  const [error,             setError]             = useState("");
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isSettingOpen,     setIsSettingOpen]     = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isUpgrading,        setIsUpgrading]        = useState(false);
  const user = JSON.parse(localStorage.getItem("user"));
  const dropdownRef  = useRef(null);
  const profileBtnRef = useRef(null);

  /* redux */
  const { renderPlan, currentPlan, subscriptionStatus } = useSelector(
    (state) => state.commonReducer
  );

  /* derived */
  const currentPlanDetails = currentPlan?.plan?.plan_detail || null;
  const createdAt          = currentPlan?.plan?.createdAt ? new Date(currentPlan.plan.createdAt) : null;
  const isCancelled        = currentPlan?.plan?.subscription_status === 2;

  const expirationDate = React.useMemo(() => {
    if (!currentPlanDetails || !createdAt) return null;
    return addIntervalToPurchaseDate(
      createdAt,
      currentPlanDetails.interval_count || 0,
      currentPlanDetails.interval || "month"
    );
  }, [currentPlanDetails, createdAt]);

  const currentDate  = new Date();
  const isPlanActive = expirationDate ? currentDate < expirationDate : false;
  const expiryString  = expirationDate ? `Expires ${formatExpiryDate(expirationDate)}` : "";
  const interval      = currentPlanDetails?.interval_count;
  const renewalString = expirationDate
    ? `Renews ${formatExpiryDate(expirationDate)}`
    : "";
  const currentPlanInterval =
    currentPlanDetails?.price === 99 ? "/ week" :
    currentPlanDetails?.price === 199 ? "/ month" : "";

  const remainingDays = React.useMemo(() => {
    if (!expirationDate) return 0;
    const timeDiff = expirationDate.getTime() - new Date().getTime();
    return Math.max(0, Math.ceil(timeDiff / (1000 * 3600 * 24)));
  }, [expirationDate]);

  /* plan features list */
  const PLAN_FEATURES = [
    "One active title",
    "Unlimited 4K renders",
    "All broadcast frame rates",
    "Scrolling credits + end cards",
    "Custom font & logo storage",
  ];

  /* effects */
  useEffect(() => {
    dispatch(getCurrentPlan());
    getData();
  }, [dispatch]);

  useEffect(() => {
    dispatch(getRendersPlan(user));
    dispatch(getCurrentPlan());
  }, []);

  /* close dropdown on scroll or outside click */
  useEffect(() => {
    const handleScroll = () => { if (isSettingOpen) setIsSettingOpen(false); };
    const handleClickOutside = (e) => {
      if (
        isSettingOpen &&
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        profileBtnRef.current && !profileBtnRef.current.contains(e.target)
      ) setIsSettingOpen(false);
    };
    if (isSettingOpen) {
      window.addEventListener("scroll", handleScroll, true);
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSettingOpen]);

  const getData = async () => {
    setIsLoading(true);
    try {
      const response = await getRequest("/user/getUserStripeDetails");
      if (response.data) {
        setUserStripeData(response.data.invoices || []);
        setPaymentMethod(response.data.paymentMethod || null);
      }
      setIsLoading(false);
    } catch (err) {
      console.error("Error fetching stripe data:", err);
      setIsLoading(false);
      setError("Network problem");
    }
  };

  const handleUpdateCard = async () => {
    if (isUpdatingCard) return;
    setIsUpdatingCard(true);
    try {
      const response = await postRequest("/user/createBillingPortalSession", {});
      if (response?.data?.success && response.data.url) {
        window.location.href = response.data.url;
        return;
      }
      toast(
        <CustomToast
          title=""
          content={response?.data?.message || "Unable to open card update. Try again."}
        />,
        defaultcustomToatStyle
      );
    } catch (err) {
      console.error("Error opening billing portal:", err);
      toast(
        <CustomToast title="" content="Unable to open card update. Try again." />,
        defaultcustomToatStyle
      );
    } finally {
      setIsUpdatingCard(false);
    }
  };

  /* cancel handler (called from modal) */
  const handleCancell = async (reason) => {
    if (!currentPlan?.plan?.order_id) {
      toast(
        <CustomToast title="" content={"No active subscription found"} />,
        defaultcustomToatStyle
      );
      return;
    }
    try {
      const res = await dispatch(
        CancellSubscription(currentPlan.plan.order_id, reason, "billing_page")
      );
      if (currentPlan?.plan?.subscription_status === 1 && res.data?.success) {
        toast(
          <CustomToast title="" content={"Subscription cancelled successfully"} />,
          defaultcustomToatStyle
        );
        dispatch(getCurrentPlan());
      }
    } catch (error) {
      console.error("Error cancelling subscription:", error);
      toast(
        <CustomToast title="" content={"Failed to cancel subscription"} />,
        defaultcustomToatStyle
      );
    }
  };

  /* upgrade navigation */
  const goToPlans = () => {
    if (isDashboard) {
      navigate("/user-plans", { state: { isDashboard: true, isMoreInfo: true } });
    } else {
      navigate("/user-plans", { state: { isDashboard, isMoreInfo: true, isCard, projectId } });
    }
  };

  const navigateHandler = () => {
    const isEmptyObject =
      currentPlan &&
      Object.keys(currentPlan).length === 0 &&
      currentPlan.constructor === Object;

    if (isEmptyObject || currentPlan?.plan?.subscription_status === 2) {
      goToPlans();
    } else {
      // already subscribed — confirm that upgrading cancels the current plan
      setIsUpgradeModalOpen(true);
    }
  };

  /* cancel current plan, then go choose a new one */
  const handleUpgradeConfirm = async () => {
    if (isUpgrading) return;
    if (!currentPlan?.plan?.order_id) {
      toast(
        <CustomToast title="" content={"No active subscription found"} />,
        defaultcustomToatStyle
      );
      return;
    }
    setIsUpgrading(true);
    try {
      const res = await dispatch(
        CancellSubscription(currentPlan.plan.order_id, "Upgrading plan", "billing_page_upgrade")
      );
      if (!res?.data?.success) {
        toast(
          <CustomToast title="" content={res?.data?.message || "Failed to cancel current plan"} />,
          defaultcustomToatStyle
        );
        return;
      }
      await dispatch(getCurrentPlan());
      setIsUpgradeModalOpen(false);
      goToPlans();
    } catch (error) {
      console.error("Error cancelling subscription for upgrade:", error);
      toast(
        <CustomToast title="" content={"Failed to cancel current plan"} />,
        defaultcustomToatStyle
      );
    } finally {
      setIsUpgrading(false);
    }
  };

  /* ─── render ──────────────────────────────────────────────── */
  const CloseButton = ({ closeToast }) => (
    <i className="material-icons w-7 flex justify-center cursor-pointer not-italic" onClick={closeToast}>✖</i>
  );

  const handleBack = () => {
    if (isDashboard || !projectId) {
      navigate("/dashboard");
      return;
    }
    if (isCard) {
      navigate(`/dashboard/card/${projectId}`, {
        state: { isDashboard: false, isCard: true, projectId },
      });
      return;
    }
    navigate(`/dashboard/scroll/${projectId}`, {
      state: { isDashboard: false, isCard: false, projectId },
    });
  };

   const getUserInitials = () => {
    const firstName = userInfo?.firstName?.trim() || "";
    if (firstName) return firstName.charAt(0).toUpperCase();
    return "U"; 
  };

  const getBackgroundColor = () => "#7c5cff26";

  return (
    <div className="cp-page">
      <ToastContainer closeButton={CloseButton} />

      {/* Floating particles background */}
      <div className="cp-particles" />

      {/* ── Topbar (same as OnSubscriptionPlanList) ── */}
      <header className="cp-topbar" style={{ width: "100%", flexShrink: 0 }}>


        <div className="flex items-center gap-3 min-w-0">
                    
          <button className="arrowIcon flex items-center opacity-30 hover:opacity-100" type="button" onClick={handleBack}>
            <IoMdArrowRoundBack size={20} />
          </button>
                  
          <div className="h-[17px] w-[1px] bg-[#ffffff1a]" />
                  
            {/* <div className="flex items-center gap-2.5 hedertext min-w-0">
              <span className="w-2 h-2 rounded-full bg-[#7c5cff] shadow-[0_0_8px_rgba(124,92,255,.8)] circleIcon" />
              <span className="hedertext font-semibold text-[14px]">{'isCard' ? "Card" : "Scroll"}</span>
              <span className="hedertext">/</span>
              <span className="text-[#625e83] font-normal">Render</span>
            </div> */}
        
          <div className="w-full h-full flex items-center gap-2.5 text-sx-ink font-semibold text-[15px] tracking-tight">
            <a href="https://scrollx.io/" className="nav-logo">
              <img className="w-[35px] h-[35px]" src={New_ScrollX2025} alt="ScrollX" />
              <span className="font-body text-[18px]">ScrollX</span>
            </a>
            
          </div>
        
        
        </div>

        {/* <div className="cp-logo">
          <a href="https://scrollx.io/" className="nav-logo">
            <img className="w-[35px]" src={New_ScrollX2025} alt="ScrollX" />
            <span style={{ marginLeft: "6px", fontSize: "18px", fontWeight: "700" }}>ScrollX.io</span>
          </a>
        </div> */}

        <div className="cp-topbar-right">
          <div className="cp-topbar-meta">
            Signed in as <strong>{user?.email || "user"}</strong>
          </div>
          <button
            ref={profileBtnRef}
            type="button"
            className="w-[35px] h-[35px] flex justify-center items-center border-[1px] border-[#7c5cff4d] hover:bg-[#7c5cff40] rounded-full transition-colors"
            onClick={() => setIsSettingOpen((open) => !open)}
            style={{ backgroundColor: getBackgroundColor() }}
            aria-expanded={isSettingOpen}
            aria-haspopup="true"
          >
            <div className="flex justify-center items-center text-[#c4b2ff] text-xl font-body w-[35px] h-[35px] rounded-full">
              <span className="font-body text-[#c4b2ff] text-lg text-center">
                {getUserInitials()}
              </span>
            </div>
          </button>
        </div>
      </header>

      {/* Profile Dropdown */}
      {isSettingOpen && (
        <div className="cp-profile-dropdown" ref={dropdownRef}>
          <div className="cp-dropdown-header">
            <div className="cp-dropdown-avatar">
              {(user?.firstName?.charAt(0) || "U").toUpperCase()}
            </div>
            <div className="cp-dropdown-user">
              <div className="cp-dropdown-name">{user?.firstName} {user?.lastName}</div>
              <div className="cp-dropdown-email">{user?.email}</div>
            </div>
          </div>
          <div className="cp-dropdown-divider" />
          <div className="cp-dropdown-body">
            <button className="cp-dropdown-item" onClick={() => { setIsSettingOpen(false); navigate("/account-settings"); }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
              My Account
            </button>
            <button className="cp-dropdown-item" onClick={() => { setIsSettingOpen(false); navigate("/more_info", { state: { isDashboard: true } }); }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect><line x1="1" y1="10" x2="23" y2="10"></line></svg>
              Billing Info
            </button>
            <button className="cp-dropdown-item" onClick={() => { setIsSettingOpen(false); window.open("/help"); }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
              Help Desk
            </button>
            <a href="https://www.scrollx.io/contact" target="_blank" rel="noopener noreferrer" className="cp-dropdown-item" onClick={() => setIsSettingOpen(false)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
              Contact us
            </a>
          </div>
          <div className="cp-dropdown-divider" />
          <div className="cp-dropdown-footer">
            <button className="cp-dropdown-item cp-danger" onClick={() => {
              setIsSettingOpen(false);
              dispatch(logout(() => navigate("/")));
              dispatch(getProjects("logout"));
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="9"></line></svg>
              Log out
            </button>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {isCancelModalOpen && (
        <CancelSubscriptionModal
          onClose={() => setIsCancelModalOpen(false)}
          onConfirm={handleCancell}
        />
      )}

      {/* Upgrade Confirm Modal */}
      {isUpgradeModalOpen && (
        <UpgradeConfirmModal
          onClose={() => setIsUpgradeModalOpen(false)}
          onConfirm={handleUpgradeConfirm}
          isLoading={isUpgrading}
        />
      )}

      {/* Page body */}
      <div className="mi-container">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.85rem",
            marginBottom: "1.75rem",
          }}
        >
          <button
            type="button"
            onClick={handleBack}
            aria-label="Go back"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              flexShrink: 0,
              borderRadius: "0.65rem",
              border: "1px solid rgba(255,255,255,0.08)",
              backgroundColor: "rgba(255,255,255,0.04)",
              color: "#eee9ff",
              cursor: "pointer",
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 style={{ color: "#fff", fontSize: "clamp(1.5rem, 3vw, 1.9rem)", fontWeight: 800, letterSpacing: "-0.02em", fontFamily: "Manrope, sans-serif", margin: 0 }}>
            Billing &amp; Plans
          </h1>
        </div>

        {/* Two-column responsive grid */}
        <div className="mi-grid">

          {/* ── Left: Current Plan card ── */}
          <div className="mi-card">
            <h2 style={{ color: "#9c98bf", fontSize: "0.9rem", fontWeight: 500, marginBottom: "0.6rem", fontFamily: "Manrope,sans-serif" }}>Current Plan</h2>

            {/* Plan name */}
            <div style={{ fontSize: "1.75rem", fontWeight: 800, color: "#fff", marginBottom: "0.75rem", fontFamily: "Manrope,sans-serif" }}>
              {isPlanActive ? `${currentPlanDetails?.type} Plan` : "Basic Plan"}
            </div>

            {/* Status badge */}
            <div style={{
              display: "inline-flex", alignItems: "center",
              padding: "0.22rem 0.85rem", borderRadius: "999px",
              backgroundColor: isCancelled ? "rgba(255,80,80,0.1)" : "rgba(127,212,154,0.1)",
              border: `1px solid ${isCancelled ? "rgba(255,80,80,0.2)" : "rgba(127,212,154,0.2)"}`,
              color: isCancelled ? "#ff5f57" : "#7fd49a",
              fontSize: "0.75rem", fontWeight: 600, width: "fit-content", marginBottom: "1.5rem",
              fontFamily: "Manrope,sans-serif",
            }}>
              {isCancelled ? "Cancelled" : isPlanActive ? "Active Subscription" : "Free"}
            </div>

            {/* Features */}
            <div style={{ display: "flex", flexDirection: "column", gap: "0.55rem", marginBottom: "1.5rem" }}>
              {PLAN_FEATURES.map((f, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "0.6rem", color: "#9c98bf", fontSize: "0.875rem", fontFamily: "Manrope,sans-serif" }}>
                  <CheckCircleIcon />
                  {f}
                </div>
              ))}
            </div>

            {/* Billing summary box */}
            <div style={{ borderRadius: "0.875rem", backgroundColor: "rgba(255, 255, 255, 0.035)", border: "1px solid rgba(255, 255, 255, 0.07)", marginBottom: "1.75rem", overflow: "hidden" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.85rem 1.1rem", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                <span style={{ fontSize: "0.85rem", color: "#9c98bf", fontFamily: "Manrope,sans-serif" }}>Billing Cycle</span>
                <span style={{ fontSize: "0.85rem", color: "#fff", fontWeight: 600, fontFamily: "Manrope,sans-serif" }}>
                  {interval === 7 ? "Weekly" : interval === 30 ? "Monthly" : "—"}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.85rem 1.1rem", borderBottom: isPlanActive && currentPlanDetails?.price ? "1px solid rgba(255,255,255,0.05)" : "none" }}>
                <span style={{ fontSize: "0.85rem", color: "#9c98bf", fontFamily: "Manrope,sans-serif" }}>
                  {isCancelled ? "Expires" : "Next Payment"}
                </span>
                <span style={{ fontSize: "0.85rem", color: "#fff", fontWeight: 600, fontFamily: "Manrope,sans-serif" }}>
                  {expirationDate ? formatExpiryDate(expirationDate) : "—"}
                </span>
              </div>
              {isPlanActive && currentPlanDetails?.price && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.85rem 1.1rem" }}>
                  <span style={{ fontSize: "0.85rem", color: "#9c98bf", fontFamily: "Manrope,sans-serif" }}>Amount</span>
                  <span style={{ fontSize: "0.85rem", color: "#fff", fontWeight: 600, fontFamily: "Manrope,sans-serif" }}>
                    ${currentPlanDetails.price}.00
                  </span>
                </div>
              )}
              {!isPlanActive && (
                <div style={{ padding: "0.85rem 1.1rem", color: "#9c98bf", fontSize: "0.85rem", fontFamily: "Manrope,sans-serif" }}>
                  Please upgrade to create end credits!
                </div>
              )}
            </div>

            {/* CTA buttons */}
            <div className="mi-btn-group" style={{ display: "flex", gap: "0.75rem", marginTop: "auto" }}>
              <button
                onClick={navigateHandler}
                style={{
                  flex: 1, padding: "0.72rem 1rem", borderRadius: "0.75rem", fontSize: "0.9rem", fontWeight: 700,
                  backgroundColor: "#7c5cff", color: "#fff", border: "none", cursor: "pointer",
                  transition: "background-color 0.15s", fontFamily: "Manrope,sans-serif",
                }}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#6b4de0")}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#7c5cff")}
              >
                Upgrade
              </button>

              {currentPlan?.plan && (
                <button
                  onClick={() => !isCancelled && setIsCancelModalOpen(true)}
                  disabled={isCancelled}
                  style={{
                    flex: 1, padding: "0.72rem 1rem", borderRadius: "0.75rem", fontSize: "0.9rem", fontWeight: 600,
                    backgroundColor: "rgba(255,80,80,0.1)", color: isCancelled ? "rgba(255,95,87,0.4)" : "#ff5f57",
                    border: "1px solid rgba(255,80,80,0.25)", cursor: isCancelled ? "default" : "pointer",
                    transition: "background-color 0.15s", fontFamily: "Manrope,sans-serif",
                  }}
                  onMouseOver={(e) => { if (!isCancelled) e.currentTarget.style.backgroundColor = "rgba(255,80,80,0.18)"; }}
                  onMouseOut={(e) => { if (!isCancelled) e.currentTarget.style.backgroundColor = "rgba(255,80,80,0.1)"; }}
                >
                  {isCancelled ? "Cancelled" : "Cancel Plan"}
                </button>
              )}
            </div>
          </div>

          {/* ── Right column ── */}
          <div className="mi-col-right">

            {/* Payment Method card */}
            <div className="mi-card" style={{ flexShrink: 0 }}>
              <h2 style={{ color: "#fff", fontSize: "0.95rem", fontWeight: 600, marginBottom: "1rem", fontFamily: "Manrope,sans-serif" }}>Payment Method</h2>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.9rem 1rem", borderRadius: "0.875rem", border: "1px solid rgba(255,255,255,0.07)", backgroundColor: "rgba(255,255,255,0.025)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.875rem" }}>
                  <div style={{ width: 36, height: 36, borderRadius: "0.5rem", backgroundColor: "rgba(124,92,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <CreditCardIcon />
                  </div>
                  <div>
                    {paymentMethod?.last4 ? (
                      <>
                        <div style={{ color: "#fff", fontSize: "0.875rem", fontWeight: 600, marginBottom: "0.15rem", fontFamily: "Manrope,sans-serif" }}>
                          {formatCardBrand(paymentMethod.brand)} •••• {paymentMethod.last4}
                        </div>
                        <div style={{ color: "#9c98bf", fontSize: "0.78rem", fontFamily: "Manrope,sans-serif" }}>
                          {formatCardExpiry(paymentMethod.expMonth, paymentMethod.expYear)
                            ? `Expires ${formatCardExpiry(paymentMethod.expMonth, paymentMethod.expYear)}`
                            : "Managed via Stripe"}
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ color: "#fff", fontSize: "0.875rem", fontWeight: 600, marginBottom: "0.15rem", fontFamily: "Manrope,sans-serif" }}>No card on file</div>
                        <div style={{ color: "#9c98bf", fontSize: "0.78rem", fontFamily: "Manrope,sans-serif" }}>Add or update via Stripe</div>
                      </>
                    )}
                  </div>
                </div>
                <button
                  style={{
                    color: isUpdatingCard ? "rgba(124,92,255,0.5)" : "#7c5cff",
                    background: "none",
                    border: "none",
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    cursor: isUpdatingCard ? "default" : "pointer",
                    fontFamily: "Manrope,sans-serif",
                  }}
                  onClick={handleUpdateCard}
                  disabled={isUpdatingCard}
                >
                  {isUpdatingCard ? "Opening…" : "Update"}
                </button>
              </div>
            </div>

            {/* Billing History card */}
            <div className="mi-card">
              <h2 style={{ color: "#fff", fontSize: "0.95rem", fontWeight: 600, marginBottom: "1.25rem", fontFamily: "Manrope,sans-serif" }}>Billing History</h2>

              {isLoading ? (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1, padding: "2rem 0" }}>
                  <CgSpinner className="animate-spin" style={{ fontSize: "1.75rem", color: "#7c5cff" }} />
                </div>
              ) : error ? (
                <div style={{ color: "#ff5f57", textAlign: "center", padding: "1.5rem 0", fontSize: "0.85rem", fontFamily: "Manrope,sans-serif", flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  Error loading data: {error}
                </div>
              ) : Array.isArray(userStripeData) && userStripeData.length > 0 ? (
                <div className="mi-invoice-list">
                  {userStripeData.map((invoice, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "0.875rem 1rem", borderRadius: "0.875rem",
                        border: "1px solid rgba(255,255,255,0.07)",
                        backgroundColor: "rgba(255,255,255,0.025)",
                        flexShrink: 0,
                      }}
                    >
                      <div>
                        <div style={{ color: "#fff", fontSize: "0.875rem", fontWeight: 600, marginBottom: "0.2rem", fontFamily: "Manrope,sans-serif" }}>
                          {invoice.plan || "Subscription"}
                        </div>
                        <div style={{ color: "#9c98bf", fontSize: "0.75rem", fontFamily: "Manrope,sans-serif" }}>
                          {formatDateTime(invoice.invoiceDate) || "N/A"}
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ color: "#fff", fontSize: "0.875rem", fontWeight: 600, fontFamily: "Manrope,sans-serif" }}>
                            {invoice.amount || "N/A"}
                          </div>
                          <div style={{ color: "#7fd49a", fontSize: "0.72rem", fontWeight: 600, fontFamily: "Manrope,sans-serif" }}>Paid</div>
                        </div>
                        {invoice.invoiceLink ? (
                          <a
                            href={invoice.invoiceLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              width: 32, height: 32, borderRadius: "0.5rem", display: "flex", alignItems: "center", justifyContent: "center",
                              backgroundColor: "rgba(255,255,255,0.06)", color: "#9c98bf", textDecoration: "none",
                              border: "1px solid rgba(255,255,255,0.07)",
                              transition: "background-color 0.15s, color 0.15s",
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.12)"; e.currentTarget.style.color = "#fff"; }}
                            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.06)"; e.currentTarget.style.color = "#9c98bf"; }}
                            title="Download Invoice"
                          >
                            <DownloadIcon />
                          </a>
                        ) : (
                          <span style={{ fontSize: "0.78rem", color: "#9c98bf", fontFamily: "Manrope,sans-serif" }}>N/A</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: "#9c98bf", textAlign: "center", padding: "2rem 0", fontSize: "0.85rem", fontFamily: "Manrope,sans-serif", flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  No invoice data available
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default MoreInfoPage;
